import React, { useState } from 'react';
import { Modal, Input, Select, Checkbox, Typography, Alert, message } from 'antd';
import { SUPPORT_CATEGORIES, SUPPORT_PRIORITIES, DESCRIPTION_MAX_CHARS, humanizeEnum } from '../../utils/supportApi';

const { Text } = Typography;

/**
 * Raise a ticket with Prolense support (POST /support/tickets). The server stamps the
 * reporter's own tenantId/userId and the SLA tier — the client never sends either
 * (Commandment III). Enum options are the backend's own CATEGORIES / PRIORITIES.
 */
const RaiseTicketModal = ({ open, api, activeProject, onClose, onCreated }) => {
    const [category, setCategory] = useState(undefined);
    const [priority, setPriority] = useState('medium');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [linkProject, setLinkProject] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const reset = () => {
        setCategory(undefined);
        setPriority('medium');
        setTitle('');
        setDescription('');
        setLinkProject(false);
        setError(null);
    };

    const canSubmit = !!category && !!priority && title.trim() && description.trim();

    const submit = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const ticket = await api.createTicket({
                category,
                priority,
                title: title.trim(),
                description,
                ...(linkProject && activeProject ? { projectId: activeProject.id } : {}),
            });
            message.success('Ticket raised.');
            reset();
            onCreated(ticket);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            title="Raise a support ticket"
            okText="Raise ticket"
            okButtonProps={{ disabled: !canSubmit, loading: submitting }}
            onOk={submit}
            onCancel={() => {
                reset();
                onClose();
            }}
        >
            {error && <Alert type="error" showIcon message="Could not raise the ticket" description={error} style={{ marginBottom: 12 }} />}
            <div style={{ display: 'grid', gap: 12 }}>
                <div>
                    <Text strong>Category</Text>
                    <Select
                        aria-label="Category"
                        style={{ width: '100%' }}
                        placeholder="Select a category"
                        value={category}
                        onChange={setCategory}
                        options={SUPPORT_CATEGORIES.map((c) => ({ value: c, label: humanizeEnum(c) }))}
                    />
                </div>
                <div>
                    <Text strong>Priority</Text>
                    <Select
                        aria-label="Priority"
                        style={{ width: '100%' }}
                        value={priority}
                        onChange={setPriority}
                        options={SUPPORT_PRIORITIES.map((p) => ({ value: p, label: humanizeEnum(p) }))}
                    />
                </div>
                <div>
                    <Text strong>Title</Text>
                    <Input aria-label="Title" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <div>
                    <Text strong>Description</Text>
                    <Input.TextArea aria-label="Description" rows={5} maxLength={DESCRIPTION_MAX_CHARS} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
                {activeProject && (
                    <Checkbox checked={linkProject} onChange={(e) => setLinkProject(e.target.checked)}>
                        Link to the open project ({activeProject.companyName || activeProject.name})
                    </Checkbox>
                )}
            </div>
        </Modal>
    );
};

export default RaiseTicketModal;
